import './FilterNameRow.scss';

import { IconEdit } from '@hulu-react-style-components/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';
import bem from '../../../../utils/bem';
import CustomFilterDeleteButton from './CustomFilterDeleteButton';
import CustomFilterDeleteModal from './CustomFilterDeleteModal';
import { usePendingFilterDelete } from './hooks/usePendingFilterDelete';
import { useUpdateCustomFilter } from './hooks/useUpdateCustomFilter';
import type { CustomFilterVisibility } from './types';

export interface FilterNameRowProps {
  filterId: string;
  filterName: string;
  filterPayload?: unknown;
  onNameSaved?: (name: string) => void;
  onDeleted?: () => void;
  readOnly?: boolean;
  initialEditing?: boolean;
  skipMutation?: boolean;
  visibility?: CustomFilterVisibility;
}

const [block, element] = bem('filter-name-row');

const FilterNameRow = ({
  filterId,
  filterName,
  filterPayload,
  onNameSaved,
  onDeleted,
  readOnly = false,
  initialEditing = false,
  skipMutation = false,
  visibility,
}: FilterNameRowProps): JSX.Element => {
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [draftName, setDraftName] = useState(filterName);
  const [error, setError] = useState<string | null>(null);
  const { updateFilter } = useUpdateCustomFilter();
  const handleDeleted = useCallback(() => onDeleted?.(), [onDeleted]);
  const { pendingTarget, isDeleting, requestDelete, cancelDelete, confirmDelete } = usePendingFilterDelete({
    onDeleted: handleDeleted,
  });
  const { adsMcCustomFilterDeleteEnabled } = useFeatureFlags();
  const inputRef = useRef<HTMLInputElement>(null);

  const canShowDeleteButton = !!adsMcCustomFilterDeleteEnabled && !readOnly && !skipMutation;
  useEffect(() => {
    setDraftName(filterName);
    setIsEditing(initialEditing);
    setError(null);
    cancelDelete();
  }, [filterId, filterName, initialEditing, cancelDelete]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = useCallback(async () => {
    const trimmed = draftName.trim();
    if (!trimmed) {
      setDraftName(filterName);
      setIsEditing(false);
      setError(null);
      return;
    }
    if (trimmed === filterName) {
      setIsEditing(false);
      return;
    }
    if (skipMutation) {
      setIsEditing(false);
      setError(null);
      onNameSaved?.(trimmed);
      return;
    }
    try {
      await updateFilter(filterId, {
        filterName: trimmed,
        ...(filterPayload !== undefined && { filterPayload: filterPayload as never }),
      });
      setIsEditing(false);
      setError(null);
      onNameSaved?.(trimmed);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save name';
      const isDuplicate =
        message.toLowerCase().includes('duplicate') || message.toLowerCase().includes('already exists');
      setError(isDuplicate ? 'A filter with this name already exists' : message);
    }
  }, [draftName, filterName, filterId, filterPayload, updateFilter, onNameSaved, skipMutation]);

  const handleBlur = useCallback(() => {
    handleSave();
  }, [handleSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        setDraftName(filterName);
        setIsEditing(false);
        setError(null);
      }
    },
    [handleSave, filterName]
  );

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setError(null);
  }, []);

  const handleRequestDelete = useCallback(() => {
    requestDelete({ id: filterId, name: filterName });
  }, [requestDelete, filterId, filterName]);

  return (
    <div className={block()} data-testid="applied-custom-filter-name-row">
      <div className={element('content')}>
        {isEditing ? (
          <input
            ref={inputRef}
            className={element('input')}
            type="text"
            value={draftName}
            onChange={(e): void => setDraftName(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            data-testid="filter-name-input"
            autoFocus
          />
        ) : (
          <>
            <span className={element('name')} data-testid="filter-name-display">
              {filterName}
            </span>
            {!readOnly && (
              <button
                className={element('edit-btn')}
                onClick={handleEdit}
                aria-label="Edit filter name"
                data-testid="filter-name-edit-btn"
              >
                <IconEdit width="14px" height="14px" title="Edit" />
              </button>
            )}
            {canShowDeleteButton && (
              <CustomFilterDeleteButton
                tooltipId={`filter-name-delete-tooltip-${filterId}`}
                filterName={filterName}
                visibility={visibility}
                onClick={handleRequestDelete}
                iconSize="14px"
                dataTestId="filter-name-delete-btn"
              />
            )}
          </>
        )}
      </div>
      {error && (
        <div className={element('error')} data-testid="filter-name-error">
          {error}
        </div>
      )}
      {canShowDeleteButton && (
        <CustomFilterDeleteModal
          visible={!!pendingTarget}
          filterName={filterName}
          loading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default FilterNameRow;
